import { DEFAULT_SOUNDS, SOUND_CATEGORIES, resolveSoundId } from "../sounds/catalog";
import { useEffect, useState } from "react";

export const PREFERENCES_STORAGE_KEY = "fox-crafting-preferences";
export const DAILY_PRESETS_FILTER_STORAGE_KEY = "fox-crafting-daily-presets-only";
export const DEFAULT_PREFERENCES = Object.freeze({
  isConfettiOn: true,
  isShowScore: true,
  isDashboardEnabled: false,
  isSoundOn: true,
  soundSelections: DEFAULT_SOUNDS,
  isVibrationOn: false,
  isQuickNextOn: false,
  warningTimer: 8,
  isTaskHighlighed: true,
  visualWarningMode: 1,
  isIntervalAlertEnabled: false,
  intervalAlertSeconds: 120,
});

function readPreferences() {
  try {
    const saved = JSON.parse(localStorage.getItem(PREFERENCES_STORAGE_KEY)) || {};
    const preferences = { ...DEFAULT_PREFERENCES };
    // Only restore known settings with valid types; task/preset data is never
    // read or changed here. Missing or invalid settings use website defaults.
    Object.keys(preferences).forEach((key) => {
      if (typeof preferences[key] === "boolean" && typeof saved[key] === "boolean") {
        preferences[key] = saved[key];
      } else if (typeof preferences[key] === "number" &&
                 typeof saved[key] === "number" && Number.isFinite(saved[key]) && saved[key] >= 0) {
        preferences[key] = key === "intervalAlertSeconds" ? Math.max(5, Math.min(999, saved[key])) : saved[key];
      }
    });
    preferences.visualWarningMode = Number.isInteger(saved.visualWarningMode) && saved.visualWarningMode >= 0 && saved.visualWarningMode <= 4
      ? saved.visualWarningMode : saved.isTaskHighlighed === false ? 0 : 1;
    preferences.soundSelections = Object.fromEntries(SOUND_CATEGORIES.map(({ id }) =>
      [id, resolveSoundId(id, saved.soundSelections?.[id])]));
    return preferences;
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export default function usePreferences() {
  const [preferences, setPreferences] = useState(readPreferences);
  useEffect(() => {
    try {
      localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // Preferences still work for this session when browser storage is blocked.
    }
  }, [preferences]);

  const setPreference = (key, value) => {
    setPreferences((previous) => ({ ...previous, [key]: value }));
  };
  // Replace just the settings record. Never use localStorage.clear(): presets,
  // pending cloud changes, authentication, and unrelated browser data must stay.
  const resetPreferences = () => {
    try {
      localStorage.removeItem(DAILY_PRESETS_FILTER_STORAGE_KEY);
    } catch {
      // Reset the current preferences even when browser storage is unavailable.
    }
    setPreferences({ ...DEFAULT_PREFERENCES });
  };
  return { preferences, setPreference, resetPreferences };
}
